/**
 * Unified work order status vocabulary for hub-native and legacy Megaventory rows.
 * Display labels and filter semantics use these values; raw hub_status / mv_status stay in the DB.
 */

export type UnifiedWorkOrderStatus =
  | "draft"
  | "open"
  | "in_process"
  | "partially_received"
  | "complete"
  | "cancelled"
  | "unknown";

export const UNIFIED_WORK_ORDER_STATUS_LABELS: Record<UnifiedWorkOrderStatus, string> = {
  draft: "Draft",
  open: "Open",
  in_process: "In process",
  partially_received: "Partially received",
  complete: "Complete",
  cancelled: "Cancelled",
  unknown: "Unknown",
};

export type WorkOrderStatusSourceRow = {
  execution_mode?: string | null;
  hub_status?: string | null;
  mv_status?: string | null;
};

export function isHubNativeWorkOrderExecutionMode(executionMode: string | null | undefined): boolean {
  return (executionMode ?? "").trim() === "hub_native";
}

/** Collapse spacing, underscores, and case for status matching. */
export function normalizeWorkOrderStatusKey(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase().replace(/[\s_]+/g, "");
}

export function unifiedWorkOrderStatusFromRaw(raw: string | null | undefined): UnifiedWorkOrderStatus {
  const key = normalizeWorkOrderStatusKey(raw);
  if (key === "") return "unknown";

  if (key === "draft" || key === "pending") return "draft";
  if (key === "open" || key === "approved") return "open";
  if (key === "inprocess") return "in_process";
  if (key === "partiallyreceived" || key === "inprocesspartiallycompleted") return "partially_received";
  if (
    key === "complete" ||
    key === "completed" ||
    key === "closed" ||
    key === "fullyreceived" ||
    key === "verifiedreceived"
  ) {
    return "complete";
  }
  if (key === "cancelled" || key === "canceled") return "cancelled";
  if (key === "verified") return "open";

  return "unknown";
}

export function rawWorkOrderStatusFromRow(row: WorkOrderStatusSourceRow): string | null {
  if (isHubNativeWorkOrderExecutionMode(row.execution_mode)) {
    return row.hub_status ?? null;
  }
  return row.mv_status ?? null;
}

export function unifiedWorkOrderStatusFromRow(row: WorkOrderStatusSourceRow): UnifiedWorkOrderStatus {
  return unifiedWorkOrderStatusFromRaw(rawWorkOrderStatusFromRow(row));
}

export function unifiedWorkOrderStatusLabel(status: UnifiedWorkOrderStatus): string {
  return UNIFIED_WORK_ORDER_STATUS_LABELS[status];
}

export type UnifiedWorkOrderStatusBadgeTone =
  | "neutral"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "muted";

export function unifiedWorkOrderStatusBadgeTone(
  status: UnifiedWorkOrderStatus,
): UnifiedWorkOrderStatusBadgeTone {
  switch (status) {
    case "draft":
    case "open":
      return "info";
    case "in_process":
    case "partially_received":
      return "warning";
    case "complete":
      return "muted";
    case "cancelled":
      return "danger";
    default:
      return "muted";
  }
}

export function isUnifiedWorkOrderStatusActive(status: UnifiedWorkOrderStatus): boolean {
  return (
    status === "draft" ||
    status === "open" ||
    status === "in_process" ||
    status === "partially_received"
  );
}

export function isUnifiedWorkOrderStatusComplete(status: UnifiedWorkOrderStatus): boolean {
  return status === "complete";
}

export function isUnifiedWorkOrderStatusCancelled(status: UnifiedWorkOrderStatus): boolean {
  return status === "cancelled";
}

/** Exclude legacy build_order rows mirrored from hub-native external work orders. */
export const BUILD_ORDER_SUBORDINATE_MV_EXCLUSION_SQL = `
  and not (
    coalesce(public.build_order.execution_mode, 'legacy_mv') <> 'hub_native'
    and public.build_order.mv_id is not null
    and exists (
      select 1
        from public.build_order_child
        inner join public.build_order hub_parent
          on hub_parent.id = public.build_order_child.build_order_id
         and hub_parent.execution_mode = 'hub_native'
       where public.build_order_child.source_system = 'megaventory'
         and public.build_order_child.external_id = public.build_order.mv_id::text
    )
  )`;
